// The console shell's federation expose ('./jobsBranch'): the shell-owned
// jobs branch, for hosts that embed console content. The enterprise host
// mounts the cloud-ui route tree and grafts this branch over its jobs route
// — the same surgery the shell itself runs in src/router.tsx.
//
// SELF-CONTAINED BY DESIGN. The consuming host provides only the shared
// federation singletons (react, react-dom, react-router, @chakra-ui/react,
// @emotion/react, next-themes); everything else the branch needs rides here:
//  - react-query is deliberately NOT federation-shared, so the branch brings
//    its own QueryClientProvider from THIS bundle's react-query copy — the
//    hooks in pages/jobs read context from that copy, and MyJobs'
//    useQueryClient().fetchQuery (CSV export) needs the same instance the
//    tRPC options proxy is bound to (lib/trpc.ts).
//  - legacy-ui's `toaster` is a module singleton whose outlet must come from
//    the same bundle copy: the host renders its OWN legacy-ui <Toaster/>, a
//    different module instance that never sees these toasts.
// The shell itself does NOT use this wrapper — its App provides the query
// client and AppLayout renders the Toaster; wrapping there would subscribe
// two outlets to one store and paint every toast twice.
//
// BOUNDARY RULE: nothing in this module's import graph may touch remote/*,
// src/theme.ts or src/router.tsx — those top-level-await the cloud-ui remote
// and would drag a second federation chain into the consuming host.

import { Toaster } from '@ionq/legacy-ui';
import { QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router';
import { replaceJobsBranch as replaceWithinTree } from '../lib/shellRoutes';
import { queryClient } from '../lib/trpc';

function EmbeddedJobsProviders({ children }: { children: ReactNode }) {
    return (
        <QueryClientProvider client={queryClient}>
            {children}
            <Toaster />
        </QueryClientProvider>
    );
}

/** Replaces the `jobs` branch of a cloud-ui route tree (wherever it nests)
 * with the shell-owned, provider-wrapped one. Same contract as the shell's
 * replaceJobsBranch, with the opposite failure posture: `found: false` means
 * the remote reshaped its tree — an embedding host should keep the
 * unmodified tree (its runtime fallback is the federated jobs page), not
 * adopt the shell's boot-fatal stance. */
export function replaceJobsBranch(routes: RouteObject[]): {
    routes: RouteObject[];
    found: boolean;
} {
    return replaceWithinTree(routes, (branch) => ({
        ...branch,
        element: (
            <EmbeddedJobsProviders>{branch.element}</EmbeddedJobsProviders>
        ),
    }));
}
